import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useContext } from "react";
import { LoginContext } from "../LoginContext";
import { getBaseUrl, getPythonBaseUrl } from "../config/apiConfig";
import {
  Calendar,
  FileText,
  MapPin,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Trash2,
  Box,
  Download,
  Move3D,
  StickyNote,
  Save,
  ChevronDown,
  ChevronUp,
  X,
  FileDown as FileDownload,
  CloudUpload,
  Sparkles,
} from "lucide-react";
import { saveAs } from "file-saver";
import { apiService } from "../backend/api";
import LoginChecker from "../components/LoginCkecker";
import useProjectDetails from "../hooks/useProjectDetails";
import ProjectDetailsSkeleton from "./skeletons/ProjectDetailsSkeleton";

const statusMap = {
  // New granular statuses
  queued: { labelKey: "projectDetails.statusQueued", color: "bg-slate-500" },
  uploading: { labelKey: "projectDetails.statusUploading", color: "bg-blue-500" },
  processing: { labelKey: "projectDetails.statusProcessing", color: "bg-amber-500" },
  review: { labelKey: "projectDetails.statusReview", color: "bg-purple-500" },
  ready: { labelKey: "projectDetails.statusReady", color: "bg-green-500" },
  rejected: { labelKey: "projectDetails.statusRejected", color: "bg-rose-400" },
  problem: { labelKey: "projectDetails.statusProblem", color: "bg-red-500" },
  // Legacy compatibility
  inprogress: { labelKey: "projectDetails.statusProcessing", color: "bg-amber-500" },
  finished: { labelKey: "projectDetails.statusReady", color: "bg-green-500" },
};

const ProjectDetails = () => {
  const { t } = useTranslation();
  const { showNotification, confirm } = useNotification();
  const navigate = useNavigate();
  LoginChecker();
  const { loginInfo } = useContext(LoginContext);
  const username = loginInfo?.username;
  const { id } = useParams();

  // Use React Query custom hook
  const { data: project, isLoading: loading, error: queryError, refetch: refetchProject } = useProjectDetails(id);
  const { deleteProject, isDeleting } = useProjectDetails(id);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewExpired, setPreviewExpired] = useState(false);
  const [previewExpiredMessage, setPreviewExpiredMessage] = useState(null);
  const [showTempStorageInfo, setShowTempStorageInfo] = useState(false);

  // Project notes state (only for cloud storage users)
  const [showNotes, setShowNotes] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Cloud sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [nextcloudConnected, setNextcloudConnected] = useState(false);
  const [isFreeUser, setIsFreeUser] = useState(false);
  const [userStatusLoaded, setUserStatusLoaded] = useState(false);

  // Download/credit consumption state
  const [isConsuming, setIsConsuming] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState("OBJ+PNG");
  const [isDownloading, setIsDownloading] = useState(false);
  const iframeRef = useRef(null);
  const downloadTokenRef = useRef(null);
  const [projectNotes, setProjectNotes] = useState({
    bodyRegion: "",
    indication: "",
    clinicalFindings: "",
    diagnosis: "",
    treatmentRecommendations: "",
    followUpNotes: "",
    scalingFactor: "",
    qualityNotes: "",
    exportFormats: "",
    caseNumber: "",
    department: "",
    referringPhysician: "",
    processingStatus: "",
    additionalNotes: "",
  });

  // Calculate remaining days for temp storage (max 7 days from processing date)
  const getTempStorageInfo = () => {
    if (!project?.dateProcessed && !project?.date) return { daysLeft: 0, expired: true };
    const processedDate = new Date(project.dateProcessed || project.date);
    const expiryDate = new Date(processedDate.getTime() + 7 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const msLeft = expiryDate.getTime() - now.getTime();
    const daysLeft = Math.max(0, Math.ceil(msLeft / (24 * 60 * 60 * 1000)));
    return { daysLeft, expired: msLeft <= 0, expiryDate };
  };

  // Check if user has Nextcloud connected + determine free/paid plan
  useEffect(() => {
    const checkUserStatus = async () => {
      if (project && !project.nextcloudSyncedAt && (project.status === 'ready' || project.status === 'review')) {
        try {
          const [ncStatus, scanStatus] = await Promise.all([
            apiService.getNextcloudStatus(),
            apiService.getScanAttemptsRemaining(),
          ]);
          setNextcloudConnected(ncStatus.connected === true);
          setIsFreeUser(scanStatus.unlimited === false);
        } catch {
          setNextcloudConnected(false);
        } finally {
          setUserStatusLoaded(true);
        }
      }
    };
    checkUserStatus();
  }, [project?.nextcloudSyncedAt, project?.status]);

  // Sync temp project to HealthCloud. The backend now uses an idempotent
  // sync service (see services/nextcloudSyncService.js) and returns the
  // resulting lifecycle state — instead of forcing a full page reload we
  // just refetch the project so the badge re-renders with the new status
  // (the useProjectDetails hook also polls every 5s while "pending").
  const handleSyncToCloud = async () => {
    if (!project?.jobId) return;
    setIsSyncing(true);
    try {
      await apiService.syncProjectToCloud(project.jobId);
      showNotification(t("projectDetails.syncSuccess"), "success");
    } catch (err) {
      const status = err?.status;
      const msg = err?.message || "";
      if (status === 409 || msg.includes("läuft bereits")) {
        showNotification(t("projectDetails.syncInProgress", "Synchronisierung läuft bereits."), "info");
      } else {
        showNotification(t("projectDetails.syncError") + msg, "error");
      }
    } finally {
      setIsSyncing(false);
      // Always refetch — even on error the backend may have updated the
      // sync status to "failed" with a new last-error, which we want to
      // show in the badge.
      refetchProject();
    }
  };

  const error = queryError?.message;

  // Generate or use stored preview URL when project is ready or in review
  useEffect(() => {
    const generatePreviewUrl = async () => {
      // Allow preview for both 'ready' and 'review' status
      if ((project?.status === 'ready' || project?.status === 'review') && project?.jobId) {
        setLoadingPreview(true);
        setPreviewExpired(false);
        setPreviewExpiredMessage(null);
        try {
          // Always generate a fresh preview URL to check subscription/Nextcloud status
          // (Nextcloud viewer for paid users with cloud storage, Python backend for others)
          const category = project.category || 'default';
          const subpath = `${category}/${project.jobId}`;
          console.log(`🔗 [ProjectDetails] Generating preview URL with subpath: ${subpath}`);

          const response = await apiService.request(`${getBaseUrl()}/api/generate-preview-url`, {
            method: 'POST',
            body: JSON.stringify({
              projectId: project.jobId,
              subpath: subpath
            })
          });
          setPreviewUrl(response.url);
          console.log(`🔗 [ProjectDetails] Preview source: ${response.viewerSource}`);
        } catch (err) {
          console.error('Failed to generate preview URL:', err);
          // Check if the error is due to expiration (410 Gone status)
          if (err.status === 410 || err.message?.includes('expired')) {
            setPreviewExpired(true);
            setPreviewExpiredMessage(err.message || 'Die Vorschau ist abgelaufen.');
          }
          // Fallback to null, will show placeholder
          setPreviewUrl(null);
        } finally {
          setLoadingPreview(false);
        }
      }
    };

    generatePreviewUrl();
  }, [project?.status, project?.jobId, project?.category]);

  // Listen for screenshot from 3D viewer iframe and save as preview image
  useEffect(() => {
    const handleScreenshotMessage = async (event) => {
      // Verify the message is a screenshot from our 3D viewer
      if (event.data?.type !== '3d-preview-screenshot' || !event.data?.screenshot) {
        return;
      }

      // Only save if project exists and doesn't already have an image
      if (!project?._id || project?.image) {
        console.log('📸 [ProjectDetails] Skipping screenshot - image already exists or no project');
        return;
      }

      console.log('📸 [ProjectDetails] Received screenshot from 3D viewer, saving...');

      try {
        const response = await apiService.request(`${getBaseUrl()}/api/user/projects/${project._id}/preview`, {
          method: 'PUT',
          body: JSON.stringify({ image: event.data.screenshot })
        });

        if (response.updated) {
          console.log('📸 [ProjectDetails] Preview image saved successfully');
        } else {
          console.log('📸 [ProjectDetails] Preview image already existed');
        }
      } catch (err) {
        console.error('📸 [ProjectDetails] Failed to save preview image:', err);
      }
    };

    window.addEventListener('message', handleScreenshotMessage);
    return () => window.removeEventListener('message', handleScreenshotMessage);
  }, [project?._id, project?.image]);

  // Listen for download results from 3D viewer iframe
  useEffect(() => {
    const handleDownloadResult = (event) => {
      if (event.data?.type !== 'download-3d-model-result') return;

      setIsDownloading(false);

      if (event.data.success) {
        try {
          const binaryString = atob(event.data.data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: event.data.mimeType });
          saveAs(blob, event.data.filename);
          showNotification(t("projectDetails.downloadSuccess"), "success");
        } catch (err) {
          console.error('Failed to process download data:', err);
          showNotification(t("projectDetails.downloadFailed"), "error");
        }
      } else {
        console.error('Export failed:', event.data.error);
        showNotification(t("projectDetails.exportFailed") + (event.data.error || ''), "error");
      }
    };

    window.addEventListener('message', handleDownloadResult);
    return () => window.removeEventListener('message', handleDownloadResult);
  }, [showNotification, t]);

  // Load project notes when notes panel is opened (only for cloud storage users)
  useEffect(() => {
    const loadNotes = async () => {
      if (showNotes && project?.nextcloudPath && project?.jobId) {
        setLoadingNotes(true);
        try {
          const response = await apiService.request(`${getBaseUrl()}/api/nextcloud/project-notes/${project.jobId}`);
          if (response.success && response.notes) {
            setProjectNotes(response.notes);
          }
        } catch (err) {
          console.error('Failed to load project notes:', err);
          // Don't show error - just use empty notes
        } finally {
          setLoadingNotes(false);
        }
      }
    };

    loadNotes();
  }, [showNotes, project?.nextcloudPath, project?.jobId]);

  // Save project notes
  const saveProjectNotes = async () => {
    if (!project?.jobId || !project?.nextcloudPath) return;

    setSavingNotes(true);
    try {
      await apiService.request(`${getBaseUrl()}/api/nextcloud/project-notes/${project.jobId}`, {
        method: 'PUT',
        body: JSON.stringify({ notes: projectNotes })
      });
      showNotification(t("projectDetails.notesSaved"), "success");
    } catch (err) {
      console.error('Failed to save project notes:', err);
      showNotification(t("projectDetails.notesSaveError"), "error");
    } finally {
      setSavingNotes(false);
    }
  };

  // Update a note field
  const updateNote = (field, value) => {
    setProjectNotes(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    const confirmed = await confirm(t("projectDetails.deleteConfirm"));
    if (!confirmed) return;

    try {
      await deleteProject();
      navigate("/projects");
    } catch (err) {
      showNotification(err.message, "error");
    }
  };

  // Initiate download flow - consume credits then show format modal
  const initiateDownload = async () => {
    if (!project?.jobId) {
      showNotification(t("projectDetails.projectNotAvailable"), "error");
      return;
    }

    setIsConsuming(true);
    try {
      const result = await apiService.consumeDownload(project.jobId);

      if (!result.alreadyUnlocked) {
        showNotification(t("projectDetails.creditConsumed"), "success");
      }

      // Store the server-issued download token for iframe validation
      downloadTokenRef.current = result.downloadToken;

      // Credits consumed successfully - open format selection modal
      setIsModalOpen(true);
    } catch (err) {
      console.error("Download-Fehler:", err);
      const errorMsg = err.message || t("projectDetails.downloadFailed");

      if (errorMsg.includes("bestätigt") || errorMsg.includes("nicht verfügbar")) {
        showNotification(t("projectDetails.acceptFirst"), "warning");
      } else if (errorMsg.includes("Keine verbleibenden Downloads") || errorMsg.includes("403")) {
        showNotification(t("projectDetails.noCredits"), "warning");
      } else {
        showNotification(t("projectDetails.downloadFailed") + ": " + errorMsg, "error");
      }
    } finally {
      setIsConsuming(false);
    }
  };

  // Handle format selection and trigger export via iframe postMessage
  const handleDownloadConfirm = () => {
    if (!iframeRef.current?.contentWindow) {
      showNotification(t("projectDetails.previewNotAvailable"), "error");
      return;
    }

    setIsDownloading(true);
    setIsModalOpen(false);

    iframeRef.current.contentWindow.postMessage({
      type: 'download-3d-model',
      format: downloadFormat,
      projectName: project?.title || 'model',
      downloadToken: downloadTokenRef.current
    }, '*');

    // Timeout in case the iframe never responds
    setTimeout(() => {
      setIsDownloading(false);
    }, 60000);
  };

  // Open the mesh viewer page with scaling controls
  const openScalingViewer = () => {
    if (!project?.jobId) {
      showNotification(t("projectDetails.projectNotAvailable"), "error");
      return;
    }

    const pythonBaseUrl = getPythonBaseUrl().replace(/\/+$/, '');
    const category = project.category || 'default';
    const subpath = `${category}/${project.jobId}`;

    // Open the mesh viewer (which has scaling controls) in a new tab
    const viewerUrl = `${pythonBaseUrl}/mesh-viewer?subpath=${encodeURIComponent(subpath)}`;
    window.open(viewerUrl, '_blank');
  };

  if (loading) return <ProjectDetailsSkeleton />;

  if (error) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6 sm:p-8 bg-red-50 dark:bg-red-900/20 border-2 border-red-100 dark:border-red-800/30 rounded-2xl sm:rounded-[2.5rem] flex flex-col items-center text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-black text-red-900 dark:text-red-200 mb-2">{t("projectDetails.loadError")}</h2>
        <p className="text-red-700/70 dark:text-red-400 font-medium mb-6">{error}</p>
        <button
          onClick={() => navigate("/projects")}
          className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all"
        >
          {t("projectDetails.backToProjects")}
        </button>
      </div>
    );
  }

  // Guard against undefined project after loading completes
  if (!project) {
    return (
      <div className="max-w-2xl mx-auto mt-12 p-6 sm:p-8 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-100 dark:border-amber-800/30 rounded-2xl sm:rounded-[2.5rem] flex flex-col items-center text-center">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl font-black text-amber-900 dark:text-amber-200 mb-2">{t("projectDetails.projectNotFound")}</h2>
        <p className="text-amber-700/70 dark:text-amber-400 font-medium mb-6">{t("projectDetails.projectNotFoundDesc")}</p>
        <button
          onClick={() => navigate("/projects")}
          className="px-6 py-2 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 transition-all"
        >
          {t("projectDetails.backToProjects")}
        </button>
      </div>
    );
  }

  const status = statusMap[project.status] || statusMap.processing;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-10 py-6 sm:py-12 space-y-4 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/projects")}
          className="flex items-center gap-2 text-slate-400 dark:text-slate-500 hover:text-[#0f7699] dark:hover:text-[#4fc3f7] font-bold transition-all group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          {t("projectDetails.back")}
        </button>

        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/30 rounded-2xl font-bold transition-all disabled:opacity-50"
        >
          {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={18} />}
          <span className="hidden sm:inline">{t("projectDetails.deleteProject")}</span>
        </button>
      </div>

      {/* Main Content Card */}
      <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden">
        {/* Status Badge - Above the viewer */}
        <div className="px-4 sm:px-8 py-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 flex flex-wrap items-center gap-3">
          <div className={`px-5 py-2 rounded-full text-white text-[10px] font-black uppercase tracking-widest shadow-lg ${status.color}`}>
            {t(status.labelKey)}
          </div>

          {/* Cloud Storage Badge - synced */}
          {project.nextcloudSyncedAt && project.nextcloudSyncStatus !== 'failed' && (
            <div className="px-5 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"></path>
              </svg>
              {t("projectDetails.cloudStorage")}
            </div>
          )}

          {/* Sync-in-progress badge — accept just fired, background sync is
              running. Shown until status flips to synced or failed. */}
          {!project.nextcloudSyncedAt && project.nextcloudSyncStatus === 'pending' && (project.status === 'ready' || project.status === 'review') && (
            <div className="px-5 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              {t("projectDetails.cloudSyncing", "Sync läuft...")}
            </div>
          )}

          {/* Sync-failed badge — opens an inline retry control on click. The
              click target IS the badge so the user can hit retry without
              digging through the temp-storage popup. */}
          {project.nextcloudSyncStatus === 'failed' && (project.status === 'ready' || project.status === 'review') && (
            <button
              onClick={handleSyncToCloud}
              disabled={isSyncing}
              className="px-5 py-2 rounded-full bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-red-500/20 transition-colors disabled:opacity-50"
              title={project.nextcloudSyncLastError || undefined}
            >
              {isSyncing
                ? <Loader2 size={14} className="animate-spin" />
                : <AlertCircle size={14} />}
              {isSyncing
                ? t("projectDetails.syncing", "Synchronisiere…")
                : t("projectDetails.cloudSyncFailed", "Cloud-Sync fehlgeschlagen — erneut versuchen")}
            </button>
          )}

          {/* Temp Storage Badge - When NOT synced to Nextcloud */}
          {!project.nextcloudSyncedAt && (project.status === 'ready' || project.status === 'review') && (
            <>
            <div className="relative">
              <button
                onClick={() => setShowTempStorageInfo(!showTempStorageInfo)}
                className="px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-amber-500/20 transition-colors cursor-pointer"
              >
                <AlertCircle size={14} />
                {t("projectDetails.tempStorage")}
                {(() => {
                  const { daysLeft, expired } = getTempStorageInfo();
                  return expired ? (
                    <span className="text-red-500">({t("projectDetails.expired")})</span>
                  ) : (
                    <span>({daysLeft} {daysLeft === 1 ? t("projectDetails.daysSingular") : t("projectDetails.daysPlural")})</span>
                  );
                })()}
              </button>

              {/* Info Popup */}
              {showTempStorageInfo && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-5 z-50 animate-in slide-in-from-top-2 duration-200">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-white text-sm">{t("projectDetails.tempStorageTitle")}</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {t("projectDetails.tempStorageDesc")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3 text-xs">
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800/30">
                      <p className="font-bold text-amber-800 dark:text-amber-300 mb-2">{t("projectDetails.secureFilesTitle")}</p>
                      <ol className="list-decimal list-inside space-y-1 text-amber-700 dark:text-amber-400">
                        <li>{t("projectDetails.secureStep1")}</li>
                        <li>{t("projectDetails.secureStep2")}</li>
                        <li>{t("projectDetails.secureStep3")}</li>
                      </ol>
                    </div>

                    {userStatusLoaded && !isFreeUser && nextcloudConnected && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/30">
                        <p className="font-bold text-green-800 dark:text-green-300 mb-2">{t("projectDetails.healthcloudConnected")}</p>
                        <p className="text-green-700 dark:text-green-400 mb-3">
                          {t("projectDetails.healthcloudSyncDesc")}
                        </p>
                        <button
                          onClick={handleSyncToCloud}
                          disabled={isSyncing}
                          className="w-full flex items-center justify-center gap-2 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs font-bold transition-colors disabled:opacity-50"
                        >
                          {isSyncing ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={14} />}
                          {isSyncing ? t("projectDetails.syncing") : t("projectDetails.syncToCloud")}
                        </button>
                      </div>
                    )}

                    {userStatusLoaded && !isFreeUser && !nextcloudConnected && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/30">
                        <p className="font-bold text-blue-800 dark:text-blue-300 mb-2">{t("projectDetails.permanentCloudStorage")}</p>
                        <p className="text-blue-700 dark:text-blue-400 mb-3">
                          {t("projectDetails.connectHealthcloudDesc")}
                        </p>
                        <Link
                          to="/profile/healthcloud"
                          className="w-full flex items-center justify-center gap-2 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors"
                        >
                          <CloudUpload size={14} />
                          {t("projectDetails.setupHealthcloud")}
                        </Link>
                      </div>
                    )}

                    {userStatusLoaded && isFreeUser && (
                      <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800/30">
                        <p className="font-bold text-purple-800 dark:text-purple-300 mb-2">{t("projectDetails.permanentStorage")}</p>
                        <p className="text-purple-700 dark:text-purple-400 mb-3">
                          {t("projectDetails.upgradeStorageDesc")}
                        </p>
                        <Link
                          to="/pricing"
                          className="w-full flex items-center justify-center gap-2 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition-colors"
                        >
                          <Sparkles size={14} />
                          {t("projectDetails.upgradeNow")}
                        </Link>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setShowTempStorageInfo(false)}
                    className="mt-4 w-full py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 transition-colors"
                  >
                    {t("projectDetails.understood")}
                  </button>
                </div>
              )}
            </div>

            {/* Inline HealthCloud action button next to Temp Storage badge (hidden until status loaded) */}
            {userStatusLoaded && !isFreeUser && nextcloudConnected && (
              <button
                onClick={handleSyncToCloud}
                disabled={isSyncing}
                className="px-5 py-2 rounded-full bg-green-500/10 border border-green-500/30 text-green-600 dark:text-green-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-green-500/20 transition-colors"
              >
                {isSyncing ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={14} />}
                {isSyncing ? t("projectDetails.transferring") : t("projectDetails.syncToCloud")}
              </button>
            )}
            {userStatusLoaded && !isFreeUser && !nextcloudConnected && (
              <Link
                to="/profile/healthcloud"
                className="px-5 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-600 dark:text-blue-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-blue-500/20 transition-colors"
              >
                <CloudUpload size={14} />
                {t("projectDetails.setupHealthcloud")}
              </Link>
            )}
            {userStatusLoaded && isFreeUser && (
              <Link
                to="/pricing"
                className="px-5 py-2 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 hover:bg-purple-500/20 transition-colors"
              >
                <Sparkles size={14} />
                {t("projectDetails.upgradeNow")}
              </Link>
            )}
            </>
          )}
        </div>

        {/* Project Header - Title, Date, Location & Description */}
        <div className="px-4 sm:px-8 py-6 border-b border-slate-100 dark:border-slate-800">
          <h1 className="text-xl sm:text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight leading-tight mb-3 break-all">
            {project.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-500 dark:text-slate-400 text-sm">
            <div className="flex items-center gap-2 font-bold">
              <Calendar className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7]" />
              {project.date ? new Date(project.date).toLocaleDateString() : "-"}
            </div>
            <div className="flex items-center gap-2 font-bold">
              <MapPin className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7]" />
              {project.location || "Online"}
            </div>
            {project.description && (
              <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500 italic">
                <span className="text-slate-300 dark:text-slate-600">—</span>
                {project.description}
              </div>
            )}
          </div>
        </div>

        {/* Header/Image Section */}
        <div className="relative h-72 sm:h-96 md:h-[600px] w-full bg-[#0f172a] group overflow-hidden">
          {(project.status === "ready" || project.status === "review") && previewUrl ? (
             <iframe
               ref={iframeRef}
               src={previewUrl}
               className="w-full h-full border-none"
               title="3D Preview"
               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
               allowFullScreen
             ></iframe>
          ) : (project.status === "ready" || project.status === "review") && loadingPreview ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900">
              <Loader2 className="w-12 h-12 text-[#0f7699] animate-spin" />
              <p className="mt-4 text-slate-400 font-medium">{t("projectDetails.loading3DPreview")}</p>
            </div>
          ) : (project.status === "ready" || project.status === "review") && previewExpired ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900">
              <div className="relative">
                <AlertCircle size={80} strokeWidth={1} className="text-amber-500/60" />
                <div className="absolute inset-0 blur-2xl bg-amber-500/10 rounded-full animate-pulse"></div>
              </div>
              <p className="mt-6 font-bold text-amber-500 text-sm">{t("projectDetails.previewExpired")}</p>
              <p className="mt-2 text-slate-400 text-xs max-w-sm text-center px-4">
                {previewExpiredMessage || t("projectDetails.previewExpiredDesc")}
              </p>
            </div>
          ) : project.image ? (
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110 opacity-80 group-hover:opacity-100"
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-[#0f172a] to-slate-900 text-slate-700">
              <div className="relative">
                <FileText size={80} strokeWidth={0.5} className="text-[#0f7699]/20" />
                <div className="absolute inset-0 blur-2xl bg-[#0f7699]/10 rounded-full animate-pulse"></div>
              </div>
              <p className="mt-6 font-black uppercase tracking-[0.3em] text-[10px] text-[#0f7699]/40">{t("projectDetails.previewUnavailable")}</p>
            </div>
          )}
        </div>

        {/* Actions Section for Ready Projects */}
        {project.status === 'ready' && (
          <div className="p-4 sm:p-6 bg-gradient-to-r from-[#0f7699]/5 to-[#4fc3f7]/5 dark:from-[#4fc3f7]/5 dark:to-[#0f7699]/5 border-b border-slate-100 dark:border-slate-800">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
                  <Box className="w-4 h-4 text-[#0f7699] dark:text-[#4fc3f7]" />
                  {t("projectDetails.manage3DModel")}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t("projectDetails.downloadAndEdit")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={initiateDownload}
                  disabled={isConsuming || isDownloading}
                  className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-[#0f7699] text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-[#0f7699]/90 transition-all shadow-lg shadow-[#0f7699]/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(isConsuming || isDownloading) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {isConsuming ? t("projectDetails.loading") : isDownloading ? t("projectDetails.exporting") : t("projectDetails.downloadModel")}
                </button>
                <button
                  onClick={openScalingViewer}
                  className="flex items-center gap-2 px-4 sm:px-5 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-purple-700 transition-all shadow-lg shadow-purple-600/20"
                >
                  <Move3D className="w-4 h-4" />
                  {t("projectDetails.scaling")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Project Notes Section - Only for Cloud Storage users */}
        {project.nextcloudPath && (
          <div className="border-b border-slate-100 dark:border-slate-800">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <StickyNote className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">
                    {t("projectDetails.projectNotes")}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    {t("projectDetails.manageNotes")}
                  </p>
                </div>
              </div>
              {showNotes ? (
                <ChevronUp className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400 dark:text-slate-500" />
              )}
            </button>

            {showNotes && (
              <div className="p-4 sm:p-6 pt-0 space-y-6 animate-in slide-in-from-top-2 duration-200">
                {loadingNotes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-purple-600 dark:text-purple-400 animate-spin" />
                    <span className="ml-2 text-slate-500 dark:text-slate-400">{t("projectDetails.loadingNotes")}</span>
                  </div>
                ) : (
                  <>
                    {/* Medical/Clinical Section */}
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-4 h-[2px] bg-purple-400"></span>
                        {t("projectDetails.medicalInfo")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.bodyRegion")}</label>
                          <input
                            type="text"
                            value={projectNotes.bodyRegion}
                            onChange={(e) => updateNote('bodyRegion', e.target.value)}
                            placeholder={t("projectDetails.bodyRegionPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.indication")}</label>
                          <input
                            type="text"
                            value={projectNotes.indication}
                            onChange={(e) => updateNote('indication', e.target.value)}
                            placeholder={t("projectDetails.indicationPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.clinicalFindings")}</label>
                        <textarea
                          value={projectNotes.clinicalFindings}
                          onChange={(e) => updateNote('clinicalFindings', e.target.value)}
                          placeholder={t("projectDetails.clinicalFindingsPlaceholder")}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.diagnosis")}</label>
                        <textarea
                          value={projectNotes.diagnosis}
                          onChange={(e) => updateNote('diagnosis', e.target.value)}
                          placeholder={t("projectDetails.diagnosisPlaceholder")}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.treatmentRecommendations")}</label>
                        <textarea
                          value={projectNotes.treatmentRecommendations}
                          onChange={(e) => updateNote('treatmentRecommendations', e.target.value)}
                          placeholder={t("projectDetails.treatmentPlaceholder")}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.followUpNotes")}</label>
                        <textarea
                          value={projectNotes.followUpNotes}
                          onChange={(e) => updateNote('followUpNotes', e.target.value)}
                          placeholder={t("projectDetails.followUpPlaceholder")}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all resize-none"
                        />
                      </div>
                    </div>

                    {/* Technical Section */}
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-4 h-[2px] bg-blue-400"></span>
                        {t("projectDetails.technicalDetails")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.scalingFactor")}</label>
                          <input
                            type="text"
                            value={projectNotes.scalingFactor}
                            onChange={(e) => updateNote('scalingFactor', e.target.value)}
                            placeholder={t("projectDetails.scalingFactorPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.exportFormats")}</label>
                          <input
                            type="text"
                            value={projectNotes.exportFormats}
                            onChange={(e) => updateNote('exportFormats', e.target.value)}
                            placeholder={t("projectDetails.exportFormatsPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.qualityNotes")}</label>
                        <textarea
                          value={projectNotes.qualityNotes}
                          onChange={(e) => updateNote('qualityNotes', e.target.value)}
                          placeholder={t("projectDetails.qualityNotesPlaceholder")}
                          rows={2}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all resize-none"
                        />
                      </div>
                    </div>

                    {/* Administrative Section */}
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-4 h-[2px] bg-amber-400"></span>
                        {t("projectDetails.adminData")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.caseNumber")}</label>
                          <input
                            type="text"
                            value={projectNotes.caseNumber}
                            onChange={(e) => updateNote('caseNumber', e.target.value)}
                            placeholder={t("projectDetails.caseNumberPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.department")}</label>
                          <input
                            type="text"
                            value={projectNotes.department}
                            onChange={(e) => updateNote('department', e.target.value)}
                            placeholder={t("projectDetails.departmentPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.processingStatus")}</label>
                          <input
                            type="text"
                            value={projectNotes.processingStatus}
                            onChange={(e) => updateNote('processingStatus', e.target.value)}
                            placeholder={t("projectDetails.processingStatusPlaceholder")}
                            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5">{t("projectDetails.referringPhysician")}</label>
                        <input
                          type="text"
                          value={projectNotes.referringPhysician}
                          onChange={(e) => updateNote('referringPhysician', e.target.value)}
                          placeholder={t("projectDetails.referringPhysicianPlaceholder")}
                          className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
                        />
                      </div>
                    </div>

                    {/* Additional Notes */}
                    <div className="space-y-4">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-4 h-[2px] bg-slate-400"></span>
                        {t("projectDetails.additionalNotes")}
                      </h4>
                      <textarea
                        value={projectNotes.additionalNotes}
                        onChange={(e) => updateNote('additionalNotes', e.target.value)}
                        placeholder={t("projectDetails.additionalNotesPlaceholder")}
                        rows={3}
                        className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-500/30 focus:border-slate-400 transition-all resize-none"
                      />
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
                      <button
                        onClick={saveProjectNotes}
                        disabled={savingNotes}
                        className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-xl font-bold text-sm hover:bg-purple-700 transition-all shadow-lg shadow-purple-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingNotes ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        {savingNotes ? t("projectDetails.saving") : t("projectDetails.saveNotes")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Related Models Section */}
        <div className="p-4 sm:p-8 md:p-12">
          <div className="space-y-4">
            <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest flex items-center gap-2">
              <span className="w-8 h-[2px] bg-[#0f7699] dark:bg-[#4fc3f7]"></span>
              {t("projectDetails.relatedModels")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {project.areas && project.areas.length > 0 ? (
                project.areas.map((area) => (
                  <span key={area._id} className="px-4 py-2 bg-blue-50 dark:bg-[#0f7699]/10 text-[#0f7699] dark:text-[#4fc3f7] rounded-xl text-xs font-bold border border-blue-100 dark:border-[#0f7699]/30 shadow-sm transition-all hover:scale-105">
                    {area.name}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 dark:text-slate-500 font-medium italic lowercase">{t("projectDetails.noModels")}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal: Choose Download Format */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-300">
          <div className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-[2.5rem] shadow-2xl overflow-hidden border border-white dark:border-slate-800 animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
            <div className="p-5 sm:p-8 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#0f7699] dark:bg-[#4fc3f7] rounded-xl sm:rounded-2xl flex items-center justify-center text-white shadow-lg">
                  <Download size={20} />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-[#0f7699] dark:text-[#4fc3f7]">{t("projectDetails.chooseFormat")}</h2>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="w-10 h-10 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-5 sm:p-8 md:p-10 space-y-6 sm:space-y-8">
              <div className="space-y-3 sm:space-y-4">
                {["OBJ+PNG", "STL", "3MF", "ALL"].map(format => (
                  <div
                    key={format}
                    onClick={() => setDownloadFormat(format)}
                    className={`p-4 sm:p-6 rounded-2xl sm:rounded-3xl border-2 transition-all cursor-pointer flex items-center gap-4 sm:gap-6 group ${
                      downloadFormat === format
                        ? "border-[#0f7699] dark:border-[#4fc3f7] bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10"
                        : "border-slate-100 dark:border-slate-700 hover:border-[#0f7699]/50 dark:hover:border-[#4fc3f7]/50 hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${
                      downloadFormat === format
                        ? "bg-[#0f7699] dark:bg-[#4fc3f7] text-white"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-400 group-hover:bg-[#0f7699]/10 dark:group-hover:bg-[#4fc3f7]/10 group-hover:text-[#0f7699] dark:group-hover:text-[#4fc3f7]"
                    }`}>
                      <FileDownload size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-bold transition-colors ${downloadFormat === format ? "text-[#0f7699] dark:text-[#4fc3f7]" : "text-slate-800 dark:text-white"}`}>
                        {format === "ALL" ? t("projectDetails.allFormats") : format}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {format === "OBJ+PNG" && t("projectDetails.objPngDesc")}
                        {format === "STL" && t("projectDetails.stlDesc")}
                        {format === "3MF" && t("projectDetails.threeMfDesc")}
                        {format === "ALL" && t("projectDetails.allFormatsDesc")}
                      </p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-4 transition-all ${
                      downloadFormat === format ? "border-[#0f7699] dark:border-[#4fc3f7] bg-white dark:bg-slate-900" : "border-slate-200 dark:border-slate-600"
                    }`} />
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 h-12 sm:h-14 border-2 border-slate-100 dark:border-slate-700 rounded-2xl text-slate-500 dark:text-slate-400 font-bold uppercase tracking-widest text-[11px] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                >
                  {t("projectDetails.cancel")}
                </button>
                <button
                  onClick={handleDownloadConfirm}
                  className="flex-1 h-12 sm:h-14 bg-[#0f7699] dark:bg-[#4fc3f7] text-white rounded-2xl font-bold uppercase tracking-widest text-[11px] shadow-xl shadow-[#0f7699]/20 hover:bg-[#0f7699]/90 dark:hover:bg-[#4fc3f7]/90 transition-all"
                >
                  {t("projectDetails.exportModel")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;
